import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useMemo } from "react";
import { shopify } from "@/lib/api";
import { t } from "@/lib/i18n";
import { optimizeImg, srcSet } from "@/lib/img";

export default function Lookbook() {
  const { data: allProducts = [] } = useQuery({
    queryKey: ["lookbook-products"],
    queryFn: () => shopify.products("NO"),
  });

  // Build a dedup'd image pool from real Marcini editorial photography.
  const entries = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const p of allProducts) {
      for (const img of p.images) {
        if (!seen.has(img)) {
          seen.add(img);
          list.push({ src: img, title: p.color_name || p.title, handle: p.handle });
        }
      }
    }
    return list;
  }, [allProducts]);

  return (
    <div className="pt-24 md:pt-32">
      <div className="px-5 md:px-12 mb-10 md:mb-16">
        <p className="text-eyebrow mb-3">{t.lookbook.eyebrow}</p>
        <h1 className="font-serif-editorial text-6xl md:text-[9vw] leading-[0.9]">{t.lookbook.title}</h1>
        <p className="mt-5 md:mt-6 max-w-xl text-[color:var(--marcini-fg-2)]">{t.lookbook.intro}</p>
      </div>

      <div className="px-5 md:px-12 pb-20 md:pb-24 grid grid-cols-6 auto-rows-[minmax(140px,auto)] gap-3 md:gap-6" data-testid="lookbook-grid">
        {entries.map((e, i) => {
          const layout = layoutFor(i);
          return (
            <Link to={`/products/${e.handle}`} key={e.src} className={`relative overflow-hidden group ${layout.className}`} data-testid={`lookbook-${i}`}>
              <img src={optimizeImg(e.src, 1200)} srcSet={srcSet(e.src, [600, 1000, 1400, 1800])} sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw" alt={e.title} className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.03]" loading={i < 3 ? "eager" : "lazy"} />
              <figcaption className="absolute bottom-3 left-3 right-3 text-white flex justify-between items-end">
                <span className="font-serif-editorial text-xl md:text-3xl drop-shadow-md">{e.title}</span>
                <span className="text-eyebrow bg-white/85 text-black px-2 md:px-3 py-1 opacity-0 group-hover:opacity-100 transition-opacity">{t.lookbook.shopLook}</span>
              </figcaption>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function layoutFor(i) {
  const patterns = [
    "col-span-6 md:col-span-4 row-span-2 md:aspect-auto aspect-[3/4]",
    "col-span-6 md:col-span-2 row-span-2 aspect-[3/5]",
    "col-span-3 md:col-span-3 aspect-[4/5]",
    "col-span-3 md:col-span-3 aspect-[4/5]",
    "col-span-3 md:col-span-2 aspect-[3/4]",
    "col-span-3 md:col-span-2 aspect-[3/4]",
    "col-span-6 md:col-span-2 aspect-[3/4]",
  ];
  return { className: patterns[i % patterns.length] };
}
